import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import Loader from '../components/Loader/Loader';
import PracticantesService from '../axios_services/practicantesRequerimientos.service';
import CulminacionService from '../axios_services/practicantesCulminaciones.service';
import { ButtonInsert, ButtonSearch, ButtonUpdate } from '../components/Buttons/Buttons';
import ModalConfirmar from '../components/Modal/ModalConfirmar';
import ModalPracticanteForm from '../components/Modal/ModalPracticanteForm';
import ModalPracticanteCarta from '../components/Modal/ModalPracticanteCarta';
import ModalCulminacionForm from '../components/Modal/ModalCulminacionForm';
import ModalCulminacionCertificado from '../components/Modal/ModalCulminacionCertificado';
import TabsWithTable from '../components/Tabs/TabsWithTable';
import { FileText } from 'lucide-react';
import './Induccion.css';
import './Practicantes.css';

const ESTADOS = {
  1: 'Requerimiento',
  2: 'En entrevista',
  3: 'Entrevista final',
  4: 'Terminado',
};

const ESTADOS_CULMINACION = {
  1: 'Registrado',
  2: 'En proceso de certificación',
  3: 'Certificado entregado',
  4: 'Planilla pagada',
};

const PERFILES = [5, 8, 22, 26];

function formatFechaSolo(v) {
  if (v == null || v === '') return '-';
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatFechaEjecucion(v) {
  if (v == null || v === '') return '-';
  const raw = String(v).replace(' ', 'T');
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return String(v);
  return dt.toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' });
}

function Practicantes() {
  const [state] = useContext(AppContext);
  const perfilId = Number(state.user?.perfil_id);
  const permitido = PERFILES.includes(perfilId);

  const [activeTab, setActiveTab] = useState('requerimientos');

  const [registros, setRegistros] = useState([]);
  const [areas, setAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [registrosCul, setRegistrosCul] = useState([]);
  const [opcionesReq, setOpcionesReq] = useState([]);
  const [isLoadingCul, setIsLoadingCul] = useState(false);

  const [modalForm, setModalForm] = useState({ isOpen: false, row: null });
  const [modalCarta, setModalCarta] = useState({ isOpen: false, row: null });
  const [confirmAvanzar, setConfirmAvanzar] = useState({ isOpen: false, row: null });

  const [modalCulForm, setModalCulForm] = useState({ isOpen: false, row: null });
  const [modalCert, setModalCert] = useState({ isOpen: false, row: null });
  const [confirmCul, setConfirmCul] = useState({ isOpen: false, tipo: null, row: null });

  const initialFetchDone = useRef(false);

  const loadAreas = async () => {
    try {
      const r = await PracticantesService.getAreas();
      setAreas(r.data || []);
    } catch {
      setAreas([]);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filtroPeriodo) params.periodo = filtroPeriodo;
      if (estadoFiltro) params.estado = estadoFiltro;
      const resp = await PracticantesService.listar(params);
      setRegistros(resp.data || []);
    } catch {
      setRegistros([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCulminaciones = useCallback(async () => {
    setIsLoadingCul(true);
    try {
      const resp = await CulminacionService.listar();
      setRegistrosCul(resp.data || []);
    } catch {
      setRegistrosCul([]);
    } finally {
      setIsLoadingCul(false);
    }
  }, []);

  const loadOpcionesReq = useCallback(async () => {
    try {
      const resp = await CulminacionService.requerimientosOpciones();
      setOpcionesReq(resp.data || []);
    } catch {
      setOpcionesReq([]);
    }
  }, []);

  useEffect(() => {
    if (!permitido || initialFetchDone.current) return;
    initialFetchDone.current = true;
    loadAreas();
    loadData();
  }, [permitido]);

  useEffect(() => {
    if (!permitido) return;
    if (activeTab !== 'culminacion') return;
    loadCulminaciones();
    loadOpcionesReq();
  }, [activeTab, permitido, loadCulminaciones, loadOpcionesReq]);

  const opcionesCulminacionFiltradas = () => {
    const tomados = new Set((registrosCul || []).map((c) => Number(c.id_requerimiento)));
    const row = modalCulForm.row;
    const actualId = row ? Number(row.id_requerimiento) : null;
    return (opcionesReq || []).filter((o) => {
      const id = Number(o.id_requerimiento);
      if (actualId != null && id === actualId) return true;
      return !tomados.has(id);
    });
  };

  if (!permitido) {
    return <div className="caja-flujo-container">No tiene permisos para acceder.</div>;
  }

  if (isLoading && registros.length === 0 && activeTab === 'requerimientos') return <Loader />;
  const renderTabRequerimientos = () => (
    <>
      <div className="table-container">
        <Table hover size="sm" className="induccion-table practicantes-table">
          <thead>
            <tr>
              <th>Practicante</th>
              <th>Área</th>
              <th>Estado</th>
              <th>Inicio práct.</th>
              <th>Fin práct.</th>
              <th>Observaciones</th>
              <th>Carta</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {registros.length > 0 ? (
              registros.map((r) => {
                const estado = Number(r.estado);
                const tieneCarta = Number(r.tiene_carta) === 1 && r.carta_url_publica;

                const bloqueCarta = () => {
                  if (tieneCarta) {
                    return (
                      <button
                        type="button"
                        title="Ver carta"
                        className="acta-icon-doc acta-icon-doc--ok"
                        onClick={() => window.open(r.carta_url_publica, '_blank')}
                      >
                        <FileText size={20} strokeWidth={2} aria-hidden />
                      </button>
                    );
                  }
                  if (estado === 3) {
                    return (
                      <button
                        type="button"
                        title="Subir carta (PDF)"
                        className="acta-icon-doc acta-icon-doc--falta"
                        onClick={() => setModalCarta({ isOpen: true, row: r })}
                      >
                        <FileText size={20} strokeWidth={2} aria-hidden />
                      </button>
                    );
                  }
                  return (
                    <span className="practicantes-carta-placeholder" title="Disponible en entrevista final">
                      —
                    </span>
                  );
                };

                return (
                  <tr key={r.id_requerimiento}>
                    <td>{r.nombre_practicante || '-'}</td>
                    <td>{r.area_descripcion || '-'}</td>
                    <td>{ESTADOS[estado] || estado}</td>
                    <td>{formatFechaSolo(r.fecha_inicio_practica)}</td>
                    <td>{formatFechaSolo(r.fecha_fin_practica)}</td>
                    <td className="practicantes-obs">{r.observaciones || '-'}</td>
                    <td>
                      <div className="acta-cell">{bloqueCarta()}</div>
                    </td>
                    <td>
                      <div className="induccion-acciones">
                        {estado < 3 && (
                          <button
                            type="button"
                            className="practicantes-btn-etapa"
                            onClick={() => setConfirmAvanzar({ isOpen: true, row: r })}
                          >
                            Siguiente etapa
                          </button>
                        )}
                        <ButtonUpdate title="Editar" onClick={() => setModalForm({ isOpen: true, row: r })} />
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="sin-data">
                  No hay registros.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );

  const renderTabCulminacion = () => {
    if (isLoadingCul && registrosCul.length === 0) {
      return <Loader />;
    }

    return (
      <>

        <div className="table-container">
          <Table hover size="sm" className="induccion-table practicantes-table">
            <thead>
              <tr>
                <th>Practicante</th>
                <th>Área</th>
                <th>Estado</th>
                <th>Fecha ejecución</th>
                <th>Observaciones</th>
                <th>Certificado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {registrosCul.length > 0 ? (
                registrosCul.map((r) => {
                  const estado = Number(r.estado);
                  const tieneCert = Number(r.tiene_certificado) === 1 && r.certificado_url_publica;

                  const bloqueCert = () => {
                    if (tieneCert) {
                      return (
                        <button
                          type="button"
                          title="Ver certificado"
                          className="acta-icon-doc acta-icon-doc--ok"
                          onClick={() => window.open(r.certificado_url_publica, '_blank')}
                        >
                          <FileText size={20} strokeWidth={2} aria-hidden />
                        </button>
                      );
                    }
                    if (estado === 2) {
                      return (
                        <button
                          type="button"
                          title="Subir certificado (PDF)"
                          className="acta-icon-doc acta-icon-doc--falta"
                          onClick={() => setModalCert({ isOpen: true, row: r })}
                        >
                          <FileText size={20} strokeWidth={2} aria-hidden />
                        </button>
                      );
                    }
                    return (
                      <span className="practicantes-carta-placeholder" title="Disponible en proceso de certificación">
                        —
                      </span>
                    );
                  };

                  return (
                    <tr key={r.id_culminacion}>
                      <td>{r.nombre_practicante || '-'}</td>
                      <td>{r.area_descripcion || '-'}</td>
                      <td>{ESTADOS_CULMINACION[estado] || estado}</td>
                      <td>{formatFechaEjecucion(r.fecha_ejecucion)}</td>
                      <td className="practicantes-obs">{r.observaciones || '-'}</td>
                      <td>
                        <div className="acta-cell">{bloqueCert()}</div>
                      </td>
                      <td>
                        <div className="induccion-acciones">
                          {estado === 1 && (
                            <button
                              type="button"
                              className="practicantes-btn-etapa"
                              onClick={() => setConfirmCul({ isOpen: true, tipo: 'proceso', row: r })}
                            >
                              En proceso de certificación
                            </button>
                          )}
                          {estado === 3 && (
                            <button
                              type="button"
                              className="practicantes-btn-etapa"
                              onClick={() => setConfirmCul({ isOpen: true, tipo: 'planilla', row: r })}
                            >
                              Planilla pagada
                            </button>
                          )}
                          <ButtonUpdate title="Editar" onClick={() => setModalCulForm({ isOpen: true, row: r })} />
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="sin-data">
                    No hay registros.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </>
    );
  };
  const getActionButton = () => {
    if (activeTab === 'requerimientos') {
      return <ButtonInsert onClick={() => setModalForm({ isOpen: true, row: null })} isLoading={isLoading} />;
    }
    if (activeTab === 'culminacion') {
      return <ButtonInsert onClick={() => setModalCulForm({ isOpen: true, row: null })} isLoading={isLoadingCul} />;
    }
    return null;
  };

  const tabs = [
    { key: 'requerimientos', label: 'Ingreso de practicantes', content: renderTabRequerimientos() },
    { key: 'culminacion', label: 'Culminación de prácticas', content: renderTabCulminacion() },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h1>GESTIÓN DE PRACTICANTES</h1>
      </div>

      <div className="practicantes-layout-container">

        <div className="filtros">
          <div className="campo-fecha">
            <label>Periodo:</label>
            <input type="month" value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.target.value)} />
          </div>
          <div className="campo-fecha">
            <label>Estado:</label>
            <select className="form-control" value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
              <option value="">Todos</option>
              <option value="1">Requerimiento</option>
              <option value="2">En entrevista</option>
              <option value="3">Entrevista final</option>
              <option value="4">Terminado</option>
            </select>
          </div>
        </div>


        <div className="practicantes-search-row">
          <ButtonSearch onClick={loadData} isLoading={isLoading} />
        </div>

        <div className="practicantes-insert-row">
          {getActionButton()}
        </div>

        <div className="practicantes-tabs-wrapper">
          <TabsWithTable
            tabs={tabs}
            defaultTab="requerimientos"
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
      </div>

      <ModalConfirmar
        isOpen={confirmAvanzar.isOpen}
        titulo="Confirmar"
        mensaje="¿Pasar al siguiente estado del proceso?"
        textConfirmar="Confirmar"
        textCancelar="Cancelar"
        variante="exito"
        onConfirmar={async () => {
          const row = confirmAvanzar.row;
          setConfirmAvanzar({ isOpen: false, row: null });
          if (!row) return;
          setIsLoading(true);
          try {
            await PracticantesService.avanzar(row.id_requerimiento);
            await loadData();
          } catch (error) {
            alert(error.response?.data?.message || 'No se pudo actualizar el estado.');
            await loadData();
          } finally {
            setIsLoading(false);
          }
        }}
        onCancelar={() => setConfirmAvanzar({ isOpen: false, row: null })}
      />

      <ModalConfirmar
        isOpen={confirmCul.isOpen}
        titulo="Confirmar"
        mensaje={
          confirmCul.tipo === 'planilla'
            ? '¿Marcar como planilla pagada?'
            : '¿Pasar a en proceso de certificación?'
        }
        textConfirmar="Confirmar"
        textCancelar="Cancelar"
        variante="exito"
        onConfirmar={async () => {
          const row = confirmCul.row;
          const tipo = confirmCul.tipo;
          setConfirmCul({ isOpen: false, tipo: null, row: null });
          if (!row) return;
          setIsLoadingCul(true);
          try {
            if (tipo === 'planilla') {
              await CulminacionService.planillaPagada(row.id_culminacion);
            } else {
              await CulminacionService.procesoCertificacion(row.id_culminacion);
            }
            await loadCulminaciones();
          } catch (error) {
            alert(error.response?.data?.message || 'No se pudo actualizar el estado.');
            await loadCulminaciones();
          } finally {
            setIsLoadingCul(false);
          }
        }}
        onCancelar={() => setConfirmCul({ isOpen: false, tipo: null, row: null })}
      />

      <ModalPracticanteForm
        isOpen={modalForm.isOpen}
        row={modalForm.row}
        areas={areas}
        onClose={(ok) => {
          setModalForm({ isOpen: false, row: null });
          if (ok) loadData();
        }}
        onSave={async (payload) => {
          if (modalForm.row) {
            await PracticantesService.actualizar(payload);
          } else {
            await PracticantesService.crear(payload);
          }
        }}
      />

      <ModalPracticanteCarta
        isOpen={modalCarta.isOpen}
        row={modalCarta.row}
        onClose={() => setModalCarta({ isOpen: false, row: null })}
        onSave={async (payload) => {
          setIsLoading(true);
          try {
            await PracticantesService.subirCarta(
              payload.id_requerimiento,
              payload.carta,
              payload.fecha_inicio_practica,
              payload.fecha_fin_practica
            );
            await loadData();
          } catch (error) {
            alert(error.response?.data?.message || 'Error al subir la carta.');
            await loadData();
            throw error;
          } finally {
            setIsLoading(false);
          }
        }}
      />

      <ModalCulminacionForm
        isOpen={modalCulForm.isOpen}
        row={modalCulForm.row}
        opciones={opcionesCulminacionFiltradas()}
        onClose={(ok) => {
          setModalCulForm({ isOpen: false, row: null });
          if (ok) loadCulminaciones();
        }}
        onSave={async (payload) => {
          if (modalCulForm.row) {
            await CulminacionService.actualizar(payload);
          } else {
            await CulminacionService.crear(payload);
          }
        }}
      />

      <ModalCulminacionCertificado
        isOpen={modalCert.isOpen}
        row={modalCert.row}
        onClose={() => setModalCert({ isOpen: false, row: null })}
        onSave={async (payload) => {
          setIsLoadingCul(true);
          try {
            await CulminacionService.subirCertificado(payload.id_culminacion, payload.certificado);
            await loadCulminaciones();
          } catch (error) {
            alert(error.response?.data?.message || 'Error al subir el certificado.');
            await loadCulminaciones();
            throw error;
          } finally {
            setIsLoadingCul(false);
          }
        }}
      />
    </>
  );
}

export default Practicantes;
